import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCS = [
  { key: 'identity', label: 'Documento de identidade (RG ou CNH)' },
  { key: 'cpf', label: 'CPF' },
  { key: 'address', label: 'Comprovante de residência' },
  { key: 'affiliation', label: 'Comprovante de vínculo atualizado (ano vigente)' }
];

export const EnrollmentRequestModal = ({ open, onClose, bundle, conditionLabel }) => {
  const [form, setForm] = useState({ full_name: '', email: '', whatsapp: '' });
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleFile = (key, file) => setFiles((f) => ({ ...f, [key]: file }));

  const reset = () => {
    setForm({ full_name: '', email: '', whatsapp: '' });
    setFiles({});
    setDone(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.whatsapp.trim()) {
      toast.error('Preencha nome, e-mail e WhatsApp.');
      return;
    }
    for (const d of DOCS) {
      if (!files[d.key]) {
        toast.error(`Anexe o documento: ${d.label}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('full_name', form.full_name);
      fd.append('email', form.email);
      fd.append('whatsapp', form.whatsapp);
      fd.append('bundle_id', bundle.id);
      fd.append('bundle_title', bundle.title);
      fd.append('condition_label', conditionLabel);
      DOCS.forEach((d) => fd.append(d.key, files[d.key]));
      await axios.post(`${API}/enrollment-requests`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(true);
      toast.success('Solicitação enviada com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar a solicitação. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="enrollment-request-modal">
        <DialogHeader>
          <DialogTitle className="text-white">Matrícula com desconto</DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="text-center py-8" data-testid="enrollment-request-success">
            <CheckCircle className="mx-auto text-green-400 mb-4" size={56} />
            <h3 className="text-xl font-bold mb-2">Solicitação enviada!</h3>
            <p className="text-gray-300 text-sm">
              Nossa equipe vai analisar seus documentos e entrar em contato pelo e-mail e WhatsApp informados para liberar o valor com desconto.
            </p>
            <Button onClick={handleClose} className="mt-6 bg-red-600 hover:bg-red-700" data-testid="enrollment-request-close-btn">
              Fechar
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-zinc-800/60 rounded-lg p-3 text-sm">
              <div className="text-gray-400">Formação</div>
              <div className="font-semibold">{bundle?.title}</div>
              <div className="text-teal-300 text-xs mt-1">{conditionLabel}</div>
            </div>

            <div>
              <Label className="text-gray-300">Nome completo</Label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="er-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">E-mail</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="er-email"
                />
              </div>
              <div>
                <Label className="text-gray-300">WhatsApp</Label>
                <Input
                  value={form.whatsapp}
                  onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="er-whatsapp"
                />
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-white">Documentos (PDF, JPG ou PNG — até 10MB cada)</p>
              {DOCS.map((d) => (
                <div key={d.key}>
                  <Label className="text-gray-300 text-xs">{d.label}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <label className="flex-1 cursor-pointer">
                      <div className={`flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs transition-colors ${files[d.key] ? 'border-green-500/60 text-green-300 bg-green-500/10' : 'border-zinc-600 text-gray-400 hover:border-red-500/60'}`}>
                        {files[d.key] ? <CheckCircle size={14} /> : <Upload size={14} />}
                        <span className="truncate">{files[d.key]?.name || 'Selecionar arquivo'}</span>
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                        className="hidden"
                        onChange={(e) => handleFile(d.key, e.target.files[0])}
                        data-testid={`er-file-${d.key}`}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              data-testid="er-submit"
            >
              {submitting ? (<><Loader2 className="mr-2 animate-spin" size={18} /> Enviando...</>) : 'Enviar solicitação'}
            </Button>
            <p className="text-[11px] text-gray-500 text-center">
              Ao enviar, você autoriza o uso dos documentos exclusivamente para validação da matrícula.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnrollmentRequestModal;
